import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAsyncCarLeasingStepData,
  fetchAsynUserIdentification,
  fetchAsynUserinformation,
  getCarLeasingStepData,
  getUserIdentification,
  getUserInformation,
} from "../../redux/transactions/UntappedSlice";

function ParentInformation({ data, onChangeParentInfo }) {
  // Initialize state for each field
  const defaultFormData = {
    parentName: "",
    parentPhone: "",
    parentNationalId: "",
  };
  
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({ ...defaultFormData });
  const [selectedParent, setSelectedParent] = useState("father"); // Track which parent is selected
  const userInformation = useSelector(getUserInformation);
  const userIdentification = useSelector(getUserIdentification);
  const phoneNumber = useSelector((state) => state.untapped.phoneNumber);
  const currentStepSavedData = useSelector(getCarLeasingStepData);

  // Fetch saved step data
  useEffect(() => {
    if (phoneNumber?.trim().length === 10) {
      dispatch(
        fetchAsyncCarLeasingStepData({ phone: phoneNumber.trim(), step: 5 })
      );
    }
  }, [dispatch, phoneNumber]);

  // Sync formData with saved step data
  useEffect(() => {
    if (currentStepSavedData?.step_data) {
      const parentInfo = currentStepSavedData.step_data.parent_info || {};

      if (parentInfo !== null) {
        setFormData((prev) => ({
          ...prev,
          parentName: parentInfo.name ?? "",
          parentPhone: parentInfo.phone ?? "",
          parentNationalId: parentInfo.national_id ?? "",
        }));
        
        // Set selected parent if available
        if (parentInfo.parent_type) {
          setSelectedParent(parentInfo.parent_type);
        }
      }
    }
  }, [currentStepSavedData]);

  // Fetch User Information when phone number changes
  useEffect(() => {
    if (formData.parentPhone?.length === 10) {
      dispatch(fetchAsynUserIdentification({ contact: formData.parentPhone }));
      dispatch(fetchAsynUserinformation({ contact: formData.parentPhone }));
    }
  }, [dispatch, formData.parentPhone]);

  // Update form data when user information is fetched
  useEffect(() => {
    if (userInformation && userIdentification) {
      setFormData((prev) => ({
        ...prev,
        parentName: `${userInformation.firstname ?? ""} ${userInformation.lastname ?? ""}`.trim(),
        parentNationalId: userIdentification.nid ?? "",
      }));
    }
  }, [userInformation, userIdentification]);

  // Update state only if incoming `data` changes
  useEffect(() => {
    if (data && JSON.stringify(data) !== JSON.stringify(formData)) {
      setFormData({ ...defaultFormData, ...data });
    }
  }, [data]);

  // Notify parent only if `formData` changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (JSON.stringify(formData) !== JSON.stringify(data)) {
        onChangeParentInfo(formData);
      }
    }, 500); // Debounce updates

    return () => clearTimeout(timeout);
  }, [formData, data, onChangeParentInfo]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle parent selection
  const handleParentSelect = (e) => {
    setSelectedParent(e.target.value);
  };

  return (
    <form>
      {/* Parent Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">
          Hitamo umubyeyi
        </label>
        <select
          value={selectedParent}
          onChange={handleParentSelect}
          className="w-full focus:outline-none px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
        >
          <option value="">-- Hitamo --</option>
          <option value="father">Se (Umubyeyi w'umugabo)</option>
          <option value="mother">Mama (Umubyeyi w'umugore)</option>
        </select>
      </div>

      {/* Parent Information - Only show if parent is selected */}
      {selectedParent && (
        <>
          <div className="mb-6">
            <h3 className="text-md font-semibold mb-3 border-b pb-2">
              Amakuru y'umubyeyi
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Amazina yombi
              </label>
              <input
                type="text"
                name="parentName"
                value={formData.parentName}
                onChange={handleChange}
                placeholder="Izina ry'umubyeyi"
                className="w-full focus:outline-none px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Nimero ya telefone
              </label>
              <input
                type="text"
                name="parentPhone"
                value={formData.parentPhone}
                onChange={handleChange}
                placeholder="078..."
                className="w-full focus:outline-none px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Nimero y'irangamuntu
              </label>
              <input
                type="text"
                name="parentNationalId"
                value={formData.parentNationalId}
                onChange={handleChange}
                placeholder="1234567890123456"
                className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
              />
            </div>
          </div>
        </>
      )}
    </form>
  );
}

export default ParentInformation;